import { IConstant } from "../components/dialogs/project/Constant";
import { Constants, ITag, Tags } from "../components/graph/StateTags";
import { NetworkData } from "../fstn/network";

export function getConstant(type: string, constants: Constants)
{
    const constantEntry = Object.entries(constants).find(([id,]) => id === type);
    if (constantEntry)
    {
        const [, constant] = constantEntry;
        return constant;
    }
    return undefined;
}

export function constantHasConstraint(constant: IConstant, constraintType: string)
{
    return Object.values(constant.constraints ?? {}).find((constraint) => constraint.type === constraintType) !== undefined;
}

export function updateConstraintData(constantId: string, value: string | number | boolean, constants: Constants, project: string, set: (path: string, value: unknown) => Promise<unknown>)
{
    const constant = constants[constantId];

    const orderableConstraintEntry = Object.entries(constant.constraints ?? {}).find(([id, constraint]) => constraint.type === "orderable");

    if (orderableConstraintEntry !== undefined)
    {
        const [constraintId, orderableConstraint] = orderableConstraintEntry;
        const values = Array.from(orderableConstraint.data?.values ?? []);

        if (!values.includes(value))
        {
            set(`constants/${project}/${constantId}/constraints/${constraintId}/data/values/${values.length}`, value);
        }
    }
}

export function selectionHasUniqueTag(selection: string[], tags: Tags, constraintType: string)
{
    return selection.some((state) => Object.entries(tags[state] ?? {}).find(([id, tag]) => tag.type === constraintType));
}

export function getConstantSelectValues(constant: IConstant): { [id: string]: string }
{
    const constraints = constant.constraints ?? {}
    const constraintKeys = Object.keys(constraints);
    const selectIndex = constraintKeys.find(constraint =>
        constraints[constraint].type === "select");

    return selectIndex ? constraints[selectIndex]?.data?.values ?? {} : {};
}

export function getTagDisplayValue(tag: ITag, constants: Constants)
{
    const constant = getConstant(tag.type, constants);

    if (constant)
    {
        if (constant.type === "boolean") {
            return constant.name;
        } else if (constant.type === "date") {
            return new Date(Number(tag.value)).toLocaleString();
        }
        const values = getConstantSelectValues(constant);

        return values[tag.value as string] || tag.value;
    }
    return tag.value;
}

export function getUniqueValues<T extends string | boolean | number>(type: string, tags: Tags): Array<T>
{
    return Array.from(Object.values(tags).reduce((values, stateTags) =>
    {
        Object.values(stateTags).forEach((tag) =>
        {
            if (tag.type === type)
            {
                values.add(tag.value as T);
            }
        });
        return values;
    }, new Set<T>())).sort();
}

export function getRemoveableTags(tags: Tags | undefined, selection: string[])
{
    return Object.values(Object.entries(tags ?? {})
        .filter(([state, stateTags]) => selection.includes(state)) // Filter by tags in the selection
        .map(([state, stateTags]) => stateTags)                    // map to just the tags
        .reduce((map, current) =>                                  // reduce to unique type value combinations                               
        {
            const tags = Object.values(current);
            tags.forEach((tag) =>
            {
                const id = `${tag.type}-${tag.value}`;
                if (!(id in map))
                    map[`${tag.type}-${tag.value}`] = tag;
            });
            return map;
        }, {} as { [id: string]: ITag }));
}

export type VisibleGroups = {
    [tag: string]: { [value: string]: string[] }
}

export function getVisibleGroupableTagStates(root: string, network: NetworkData, tags: Tags, constants: Constants): VisibleGroups
{
    const visibleGroups: VisibleGroups = {};

    const groupConstants = Object.keys(constants ?? {}).filter((id) => constantHasConstraint(constants[id], "groupable"));

    Object.keys(network.states || {})
        .filter(state => network.states[state].parent === root)
        .forEach(state =>
        {
            Object.values(tags[state] ?? {}).forEach((tag) =>
            {
                if (groupConstants.includes(tag.type))
                {
                    if (visibleGroups[tag.type] === undefined)
                    {
                        visibleGroups[tag.type] = {};
                    }
                    const value = String(tag.value);
                    if (visibleGroups[tag.type][value] === undefined)
                    {
                        visibleGroups[tag.type][value] = [];
                    }
                    visibleGroups[tag.type][value].push(state);
                }
            });
        });

    return visibleGroups;
}

